/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {Signal} from '@preact/signals-react';
import {Context, GridState} from './types';

/**
 * Defines the default Grid State which can be used for resetting and / or
 * re-initializing the Vehicle Manager or Vehicle Profile grid settings.
 */
export const defaultGridSettings: GridState = {
  isInitialized        : false,
  columnState          : [],
  preferredColumnState : [],
  hasUpdate            : false,
  refreshRequired      : false,
  includeSubGroups     : false,
  pageSize             : 50,
  filterModel          : {}
};

/**
 * Provides the mutation for updating the current state of the Vehicle Manager
 * or Vehicle Profile Grids based on the User's current settings.
 *
 * This mutator function can be used to update the grid state in a partial manner,
 * or to completely overwrite the current state with the provided state.
 *
 * {@link defaultGridSettings}
 * {@link GridState}
 * @param gridState The current state for which the grid state is to be set, or
 *        when the 'partial' argument is set to `true` (default), only the values
 *        that are updated in the `gridState` will be used to update the current
 *        state.
 * @param partial When `true` (default), the provided state will be merged with
 *        the current state, otherwise, when `false`, the current state will be
 *        overwritten with that of the provided state via the `gridState` arg.
 * @param signal The `signal` instance for which the grid settings are to be
 *        updated.
 * @returns The updated state of the grid based on the provided arguments.
 */
export const updateGridState = (
  gridState: Partial<GridState>, partial = true, signal: Signal): Partial<GridState> => (
  signal.value = partial ? {
    ...signal.value,
    ...gridState
  } : gridState
);
